import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { waitForAuth } from '~/atoms/authAtom';
import { ApiEndpoint, ApiEndpointDef, HTTPMethodKeys } from '../types';

axiosRetry(axios, { retries: 3 });

export const getEndpointUrl = (endpoint: ApiEndpoint) => {
  return endpoint.server.url + endpoint.path;
};

export type ExecParams<
  T extends ApiEndpointDef,
  M extends HTTPMethodKeys<T['methods']>,
> = {
  method: M extends HTTPMethodKeys<T['methods']> ? M : never;
  config?: AxiosRequestConfig;
  payload?: T['methods'][M] extends { payload: infer P } ? P : undefined;
  urlExtra?: string;
  returnAxiosResponse?: boolean;
};

export type ExecParamsSimple<
  T extends ApiEndpointDef,
  M extends HTTPMethodKeys<T['methods']>,
> = Omit<ExecParams<T, M>, 'returnAxiosResponse'> & {
  returnAxiosResponse?: false;
};
export type ExecParamsAxios<
  T extends ApiEndpointDef,
  M extends HTTPMethodKeys<T['methods']>,
> = Omit<ExecParams<T, M>, 'returnAxiosResponse'> & {
  returnAxiosResponse: true;
};

export type MethodType<T extends ApiEndpointDef> = HTTPMethodKeys<T['methods']>;

export type ExecEndpointResponse<
  T extends ApiEndpointDef,
  M extends HTTPMethodKeys<T['methods']>,
> = (T['methods'][M] extends { response: infer R } ? R : undefined) | undefined;

export function getExecEndpoint<T extends ApiEndpointDef>(
  endpoint: T['endpoint'],
) {
  return async <M extends MethodType<T>>({
    payload,
    method,
    config,
    urlExtra,
    returnAxiosResponse,
  }: ExecParamsAxios<T, M> | ExecParamsSimple<T, M>): Promise<
    ExecEndpointResponse<T, M>
  > => {
    // Wait for auth to be available (handles popup window race condition)
    const auth = await waitForAuth();

    const token = await auth?.auth.getToken({
      template: 'banqora_template', // Todo: Maybe move this to env file
    });
    const headers = config?.headers
      ? { ...config.headers, Authorization: `Bearer ${token}` }
      : { Authorization: `Bearer ${token}` };
    const url = getEndpointUrl(endpoint);
    const axiosConfig: AxiosRequestConfig = {
      ...config,
      method: method,
      headers: headers,
      url: `${url}${urlExtra ? urlExtra : ''}`,
    };
    if (payload) {
      axiosConfig['data'] = payload;
    }
    if (returnAxiosResponse) {
      try {
        const axiosResponse = await axios.request<
          unknown,
          T['methods'][M] extends { response: infer R } ? R : undefined
        >(axiosConfig);
        return axiosResponse;
      } catch (e) {
        console.log('axios error', e);

        const errorResponse = (e as AxiosError).response;
        if (errorResponse) {
          errorResponse.statusText = (e as AxiosError).message;
        }
        return errorResponse as ExecEndpointResponse<T, M>;
      }
    }
    try {
      // Axios will automatically throw an error if the response is not in the 2xx range
      const response =
        await axios.request<
          T['methods'][M] extends { response: infer R } ? R : undefined
        >(axiosConfig);

      return response.data;
    } catch (e) {
      const axiosError = e as AxiosError;

      // Ignore 429 (rate limit) and 504 (gateway timeout) errors, ADDED JUST FOR SL
      if (
        axiosError.response?.status === 429 ||
        axiosError.response?.status === 504
      ) {
        // Return undefined for these temporary errors instead of throwing
        return undefined;
      }

      // Throw BQApiError for other errors
      throw new BQApiError('Request failed', axiosError);
    }
  };
}
class BQApiError<R> extends Error {
  statusCode?: number;
  response: R | null = null;
  constructor(message: string, axiosError?: AxiosError<R>) {
    super(message);
    if (axiosError && axiosError.response) {
      this.response = axiosError.response.data;
      this.statusCode = axiosError.status;
    }
  }
}
